// One display model for ducted performance, shared by the Choose Systems
// detail panel and the comparison surfaces so the same system never shows a
// different headline in two places.
//
// Ducted systems carry two coverage metrics:
//   - design-load coverage: rated capacity against the near-worst-case design
//     load, the sizing check the quote is filtered on;
//   - bin coverage: the outdoor temperature through which the system holds
//     full output, plus the hours-weighted share of the season it carries.
//
// The design-load percent leads and the temperature finishes the sentence:
// "94% heating coverage, full heating down to 12°F". The hours figure gets its
// own line only when it disagrees with the headline — a system that covers the
// design load covers essentially every hour, and repeating that as a second
// 100% is noise. Once the system carries every conditioning hour, the
// temperature goes too: the coldest (hottest) bin it holds full output
// through is then simply the coldest (hottest) hour in the station's weather
// data — a fact about the climate, not the equipment — so the caption says
// the system covers every hour instead. What each number means lives in the
// block's tooltip instead of a caption line the card has no room for.
import type { SystemOptionType } from 'data/types';
import type {
  DuctedCoverageBasis,
  DuctedCoverageType
} from 'utils/ducted-coverage';
import {
  DUCTED_COVERAGE_UNAVAILABLE_TOOLTIP,
  ductedCoverageTooltipText,
  ductedPercentLabel,
  getDuctedCoverage
} from 'utils/ducted-coverage';
import type { DuctedBinCoverage } from 'utils/ducted-system-metrics';
import {
  readDuctedBinCoverage,
  readDuctedDesignCoverage
} from 'utils/ducted-system-metrics';

export type DuctedCoverageSideDisplay = {
  value: string;
  caption: string;
  detail: string | null;
  tooltip: string;
};

export type DuctedPerformanceDisplay = {
  heating: DuctedCoverageSideDisplay;
  cooling: DuctedCoverageSideDisplay;
};

export type DuctedHeadlineCoveragePercents = {
  heating: number | null;
  cooling: number | null;
};

export type DuctedCoverageHeadline = {
  percent: number | null;
  label: string;
  hoursPercent: number | null;
  fullOutputTemp: number | null;
};

export type DuctedCoverageHeadlines = {
  heating: DuctedCoverageHeadline;
  cooling: DuctedCoverageHeadline;
};

type SideCopy = {
  side: 'heating' | 'cooling';
  fullOutputCaption: (temp: number) => string;
  fullSeasonCaption: string;
  designLoadCaption: string;
  hoursCaption: string;
  hoursDetail: (percent: number) => string;
  fullOutputSentence: (temp: number) => string;
  fullSeasonSentence: string;
  hoursSentence: (percent: number) => string;
};

// Which figure leads a side. The design-load percent wins whenever it
// resolved; the hours figure stands in when it did not; with neither the side
// dashes out. Resolving this once keeps the value, caption, detail and
// tooltip provably in agreement about what the headline is.
type Headline =
  | {
      kind: 'design';
      percent: number;
      basis: DuctedCoverageBasis;
      hoursPercent: number | null;
    }
  | { kind: 'hours'; percent: number }
  | { kind: 'none' };

type SideState = {
  headline: Headline;
  fullOutputTemp: number | null;
  copy: SideCopy;
};

function formatTemp(temp: number): string {
  return `${Math.round(temp)}°F`;
}

// Never rounds up: 99.6% of the cooling hours presented as "100%" claims a
// season the system misses, and it reads as a contradiction beside a
// design-load percent that floored to 96%. Just under a full season the
// figure keeps the tenth the server already floored it to — "99.9%" is both
// honest and worth the character, where a bare "99%" would undersell a
// system short by a few hours a year.
function formatPercent(percent: number): string {
  if (percent < 99 || percent >= 100) {
    return `${Math.floor(percent)}%`;
  }
  return `${Number.isInteger(percent) ? percent : percent.toFixed(1)}%`;
}

const HEATING_COPY: SideCopy = {
  side: 'heating',
  fullOutputCaption: (temp) => `Full heating down to ${formatTemp(temp)}`,
  fullSeasonCaption: 'Covers every heating hour',
  designLoadCaption: 'of design heating load',
  hoursCaption: 'of heating hours',
  hoursDetail: (percent) => `${formatPercent(percent)} of heating hours`,
  fullOutputSentence: (temp) =>
    `It holds full heating output down to ${formatTemp(temp)}.`,
  fullSeasonSentence:
    'It holds full heating output through every heating hour in your area, backup heat included.',
  hoursSentence: (percent) =>
    `Across a year it carries ${formatPercent(
      percent
    )} of the heating hours, backup heat included.`
};

const COOLING_COPY: SideCopy = {
  side: 'cooling',
  fullOutputCaption: (temp) => `Full cooling up to ${formatTemp(temp)}`,
  fullSeasonCaption: 'Covers every cooling hour',
  designLoadCaption: 'of design cooling load',
  hoursCaption: 'of cooling hours',
  hoursDetail: (percent) => `${formatPercent(percent)} of cooling hours`,
  fullOutputSentence: (temp) =>
    `It holds full cooling output up to ${formatTemp(temp)}.`,
  fullSeasonSentence:
    'It holds full cooling output through every cooling hour in your area.',
  hoursSentence: (percent) =>
    `Across a year it carries ${formatPercent(percent)} of the cooling hours.`
};

function resolveHeadline(
  designPercent: number | null,
  basis: DuctedCoverageBasis | null,
  hoursPercent: number | null
): Headline {
  if (designPercent !== null && basis !== null) {
    return { kind: 'design', percent: designPercent, basis, hoursPercent };
  }
  if (hoursPercent !== null) {
    return { kind: 'hours', percent: hoursPercent };
  }
  return { kind: 'none' };
}

function buildValue({ headline }: SideState): string {
  switch (headline.kind) {
    case 'design':
      return ductedPercentLabel(headline.percent);
    case 'hours':
      return formatPercent(headline.percent);
    default:
      return '—';
  }
}

function hoursPercentOf(headline: Headline): number | null {
  switch (headline.kind) {
    case 'design':
      return headline.hoursPercent;
    case 'hours':
      return headline.percent;
    default:
      return null;
  }
}

// The full-output temperature is news only while some hours go uncovered.
// The server floors the hours figure to a tenth, so a season short by even
// a few hours reads 99.9 here and keeps its temperature.
function coversEveryHour(headline: Headline): boolean {
  const hoursPercent = hoursPercentOf(headline);
  return hoursPercent !== null && hoursPercent >= 100;
}

// The temperature finishes the sentence when there is one — unless the
// system carries every hour, when "covers every hour" is the sentence;
// otherwise the caption has to name the denominator of whichever percent
// leads.
function buildCaption({ headline, fullOutputTemp, copy }: SideState): string {
  if (fullOutputTemp !== null) {
    return coversEveryHour(headline)
      ? copy.fullSeasonCaption
      : copy.fullOutputCaption(fullOutputTemp);
  }
  return headline.kind === 'hours' ? copy.hoursCaption : copy.designLoadCaption;
}

// The hours figure earns a line only when it says something the headline
// does not. Full design-load coverage implies full hours coverage, so the
// two agreeing means the second line would just repeat the first.
function buildDetail({ headline, copy }: SideState): string | null {
  if (headline.kind !== 'design' || headline.hoursPercent === null) {
    return null;
  }
  return ductedPercentLabel(headline.percent) ===
    formatPercent(headline.hoursPercent)
    ? null
    : copy.hoursDetail(headline.hoursPercent);
}

// A system that carries every hour gets the one sentence saying so in place
// of a temperature that is only the weather's extreme and an hours figure
// that would repeat it.
function buildTooltip({ headline, fullOutputTemp, copy }: SideState): string {
  const hoursPercent = hoursPercentOf(headline);
  const fullSeason = coversEveryHour(headline);
  const sentences = [
    headline.kind === 'design'
      ? ductedCoverageTooltipText(copy.side, {
          value: headline.percent,
          basis: headline.basis
        })
      : null,
    fullSeason ? copy.fullSeasonSentence : null,
    fullOutputTemp === null || fullSeason
      ? null
      : copy.fullOutputSentence(fullOutputTemp),
    hoursPercent === null || fullSeason
      ? null
      : copy.hoursSentence(hoursPercent)
  ].filter((sentence): sentence is string => sentence !== null);
  return sentences.length === 0
    ? DUCTED_COVERAGE_UNAVAILABLE_TOOLTIP
    : sentences.join(' ');
}

function buildSide(state: SideState): DuctedCoverageSideDisplay {
  return {
    value: buildValue(state),
    caption: buildCaption(state),
    detail: buildDetail(state),
    tooltip: buildTooltip(state)
  };
}

// What a ducted surface shows when neither metric resolved: dashes with the
// unavailable explainer, the same way a single side that failed reads. A
// surface that knows it is ducted uses this instead of a null display.
export const DUCTED_PERFORMANCE_UNAVAILABLE: DuctedPerformanceDisplay = {
  heating: buildSide({
    headline: { kind: 'none' },
    fullOutputTemp: null,
    copy: HEATING_COPY
  }),
  cooling: buildSide({
    headline: { kind: 'none' },
    fullOutputTemp: null,
    copy: COOLING_COPY
  })
};

function headlinePercent(headline: Headline): number | null {
  return headline.kind === 'none' ? null : headline.percent;
}

function buildHeadline(state: SideState): DuctedCoverageHeadline {
  const { headline, fullOutputTemp, copy } = state;
  const denominator =
    headline.kind === 'hours' ? copy.hoursCaption : copy.designLoadCaption;
  return {
    percent: headlinePercent(headline),
    label:
      headline.kind === 'none'
        ? buildValue(state)
        : `${buildValue(state)} ${denominator}`,
    hoursPercent: hoursPercentOf(headline),
    fullOutputTemp
  };
}

type SideStates = {
  heating: SideState;
  cooling: SideState;
};

function buildSideStates(
  designCoverage: DuctedCoverageType | null,
  binCoverage: DuctedBinCoverage | null
): SideStates | null {
  if (!designCoverage && !binCoverage) {
    return null;
  }
  const basis = designCoverage?.basis ?? null;
  return {
    heating: {
      headline: resolveHeadline(
        designCoverage?.heatingCoveragePercent ?? null,
        basis,
        binCoverage?.heatingCoveragePercent ?? null
      ),
      fullOutputTemp: binCoverage?.minTempAtFullHeating ?? null,
      copy: HEATING_COPY
    },
    cooling: {
      headline: resolveHeadline(
        designCoverage?.coolingCoveragePercent ?? null,
        basis,
        binCoverage?.coolingCoveragePercent ?? null
      ),
      fullOutputTemp: binCoverage?.maxTempAtFullCooling ?? null,
      copy: COOLING_COPY
    }
  };
}

function sideStatesForSystem(
  system: Partial<SystemOptionType> | null | undefined
): SideStates | null {
  return buildSideStates(
    getDuctedCoverage(system) ?? null,
    system?.ductedBinCoverage ?? null
  );
}

function buildHeadlinePercents(
  states: SideStates | null
): DuctedHeadlineCoveragePercents {
  return {
    heating: states ? headlinePercent(states.heating.headline) : null,
    cooling: states ? headlinePercent(states.cooling.headline) : null
  };
}

export function ductedHeadlineCoveragePercents(
  combination: Record<string, unknown>
): DuctedHeadlineCoveragePercents {
  return buildHeadlinePercents(
    buildSideStates(
      readDuctedDesignCoverage(combination),
      readDuctedBinCoverage(combination)
    )
  );
}

export function ductedHeadlineCoveragePercentsForSystem(
  system: Partial<SystemOptionType> | null | undefined
): DuctedHeadlineCoveragePercents | null {
  const states = sideStatesForSystem(system);
  return states ? buildHeadlinePercents(states) : null;
}

export function ductedCoverageHeadlinesForSystem(
  system: Partial<SystemOptionType> | null | undefined
): DuctedCoverageHeadlines | null {
  const states = sideStatesForSystem(system);
  return states
    ? {
        heating: buildHeadline(states.heating),
        cooling: buildHeadline(states.cooling)
      }
    : null;
}

export function ductedHoursShareLabel(
  side: 'heating' | 'cooling',
  percent: number
): string {
  const copy = side === 'heating' ? HEATING_COPY : COOLING_COPY;
  return copy.hoursDetail(percent);
}

function buildDisplay(
  states: SideStates | null
): DuctedPerformanceDisplay | null {
  return states
    ? {
        heating: buildSide(states.heating),
        cooling: buildSide(states.cooling)
      }
    : null;
}

export function ductedPerformanceFromCombination(
  combination: Record<string, unknown>
): DuctedPerformanceDisplay | null {
  return buildDisplay(
    buildSideStates(
      readDuctedDesignCoverage(combination),
      readDuctedBinCoverage(combination)
    )
  );
}

export function ductedPerformanceFromSystem(
  system: Partial<SystemOptionType> | null | undefined
): DuctedPerformanceDisplay | null {
  return buildDisplay(sideStatesForSystem(system));
}
